/**
 * Geo-Redirect Manager
 *
 * Detects visitor country via Shopify's browsing_context_suggestions endpoint
 * and submits a POST to /localization so Shopify Markets resolves the URL,
 * currency, and locale itself. No hardcoded market path table — the source of
 * truth is whatever Markets the merchant has configured.
 *
 * Market metadata for the post-redirect banner is read from the
 * data-geo-markets JSON blob rendered by snippets/geo-redirect-banner.liquid.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'geo_redirect_preference';
  const BANNER_SHOWN_KEY = 'geo_redirect_banner_shown';

  function readMarketsCatalogue() {
    const node = document.getElementById('GeoRedirectMarkets');
    if (!node || !node.textContent) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('Geo-redirect: invalid markets catalogue JSON', error);
      return {};
    }
  }

  class GeoRedirectManager {
    constructor() {
      this.currentPath = window.location.pathname;
      this.isCheckout = this.currentPath.includes('/checkout');
      this.isBot = /bot|crawler|spider|googlebot|bingbot|yandex|baidu/i.test(navigator.userAgent);
      this.catalogue = readMarketsCatalogue();
    }

    async init() {
      if (this.isBot || this.isCheckout) return;
      // Never geo-redirect from a 404: return_to would carry the broken path into another market.
      // ('tempalte-404' matches the body class spelling in layout/theme.liquid.)
      if (document.body.classList.contains('tempalte-404')) return;

      const preference = this.getPreference();
      if (preference && preference.source === 'manual') return;

      if (sessionStorage.getItem(BANNER_SHOWN_KEY) === 'pending') {
        sessionStorage.setItem(BANNER_SHOWN_KEY, 'shown');
        this.showBanner();
        return;
      }

      if (sessionStorage.getItem(BANNER_SHOWN_KEY) === 'shown') return;
      if (preference && preference.confirmedAt) return;

      try {
        const detected = await this.detectLocation();
        if (detected) this.handleRedirect(detected);
      } catch (error) {
        console.warn('Geo-redirect: Detection failed', error);
      }
    }

    async detectLocation() {
      try {
        const response = await fetch(
          window.Shopify.routes.root +
            'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true'
        );
        if (!response.ok) throw new Error('Failed to fetch browsing context');

        const data = await response.json();
        const detectedCountry = data.detected_values?.country?.handle;
        const detectedLanguage = data.detected_values?.language?.handle;
        if (!detectedCountry) return null;

        return {
          country: detectedCountry,
          language: detectedLanguage || null,
          browserLanguage: navigator.language || 'en'
        };
      } catch (error) {
        console.warn('Geo-redirect: API error', error);
        return null;
      }
    }

    // Returns the language to ask Shopify for, given the detected country.
    // If Markets has the detected language available on that country, use it.
    // Otherwise, fall back to the first available language for that country
    // (i.e. let Markets pick its default) by omitting locale_code.
    pickLocaleFor(countryCode, detectedLanguage) {
      const market = this.catalogue[countryCode];
      if (!market || !Array.isArray(market.languages)) return null;
      if (detectedLanguage) {
        const match = market.languages.find(
          (lang) => lang.iso_code.toLowerCase() === detectedLanguage.toLowerCase()
        );
        if (match) return match.iso_code;
      }
      // Try the browser language prefix (e.g. "ar-EG" → "ar")
      const prefix = (navigator.language || '').slice(0, 2).toLowerCase();
      if (prefix) {
        const prefixMatch = market.languages.find(
          (lang) => lang.iso_code.slice(0, 2).toLowerCase() === prefix
        );
        if (prefixMatch) return prefixMatch.iso_code;
      }
      return null;
    }

    handleRedirect(detected) {
      const market = this.catalogue[detected.country];
      const currentCountry = (document.documentElement.getAttribute('data-current-country') || '').toUpperCase();

      // Already on the right country — confirm and exit.
      if (market && currentCountry === detected.country.toUpperCase()) {
        this.savePreference({
          country: detected.country,
          source: 'auto',
          confirmedAt: Date.now()
        });
        return;
      }

      // Country isn't configured in Markets — nothing to do.
      if (!market) return;

      const targetLocale = this.pickLocaleFor(detected.country, detected.language);

      sessionStorage.setItem(BANNER_SHOWN_KEY, 'pending');
      this.savePreference({
        country: detected.country,
        language: targetLocale,
        source: 'auto',
        confirmedAt: null,
        marketName: market.name
      });

      this.submitLocalization(detected.country, targetLocale);
    }

    submitLocalization(countryCode, localeCode) {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = (window.Shopify?.routes?.root || '/') + 'localization';
      form.style.display = 'none';

      const append = (name, value) => {
        if (value === null || value === undefined) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };

      append('form_type', 'localization');
      append('utf8', '✓');
      append('_method', 'put');
      append('return_to', window.location.pathname + window.location.search);
      append('country_code', countryCode);
      if (localeCode) append('locale_code', localeCode);

      document.body.appendChild(form);
      form.submit();
    }

    showBanner() {
      const preference = this.getPreference();
      if (!preference) return;

      const banner = document.getElementById('geo-redirect-banner');
      if (!banner) return;

      const marketNameEl = banner.querySelector('[data-market-name]');
      if (marketNameEl) {
        const fromCatalogue = this.catalogue[preference.country]?.name;
        marketNameEl.textContent = fromCatalogue || preference.marketName || 'this region';
      }

      banner.classList.add('is-visible');
      this.bindBannerEvents(banner);
    }

    bindBannerEvents(banner) {
      banner.querySelector('[data-stay-here]')?.addEventListener('click', () => {
        this.confirmPreference();
        this.hideBanner(banner);
      });
      banner.querySelector('[data-switch-market]')?.addEventListener('click', () => {
        this.openMarketSelector();
        this.hideBanner(banner);
      });
      banner.querySelector('[data-close-banner]')?.addEventListener('click', () => {
        this.confirmPreference();
        this.hideBanner(banner);
      });
    }

    hideBanner(banner) {
      banner.classList.remove('is-visible');
      banner.classList.add('is-hidden');
    }

    confirmPreference() {
      const preference = this.getPreference();
      if (!preference) return;
      preference.confirmedAt = Date.now();
      preference.source = 'banner';
      this.savePreference(preference);
    }

    openMarketSelector() {
      const selector = document.querySelector('.market-localization__button');
      if (selector) {
        selector.click();
        selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    getPreference() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    }

    savePreference(preference) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
      } catch (error) {
        console.warn('Geo-redirect: Could not save preference', error);
      }
    }

    clearPreference() {
      try {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(BANNER_SHOWN_KEY);
      } catch {
        // Ignore
      }
    }
  }

  function init() {
    new GeoRedirectManager().init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.GeoRedirectManager = GeoRedirectManager;
})();
